import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import colors from '../../../assets/theme/base/colors';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import DashboardLayout from '../../../examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';
import styles from './policyCancellation.module.scss';
import useFetchPolicyDetails from '../policyDetails/useFetchPolicyDetails';
import SoftSelect from '../../../components/SoftSelect';
import { policyCancellationReasons } from './const/policyCancellationReasons';
import { NotificationContext } from '../../../shared/context/notificationContext';
import {
  CancellationRejectCauses,
  checkForPolicyCancellationRejectCause,
} from '../policyDetails/policyDetails.helper';
import useCancelPolicy from './helpers/useCancelPolicy';
import useCancellationRequest from './helpers/useCancellationRequest';
import useFetchPolicyRefund from './helpers/useFetchPolicyRefund';
import CancelledPolicyDetails from './cancelledPolicyDetails';
import PriceDiffSection from './PriceDiffSection/PriceDiffSection';
import SoftButton from 'components/SoftButton';
import Breadcrumbs from 'examples/Breadcrumbs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Close, ExpandMore } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { policyCancellationMethodReceived } from './const/policyCancellationMethodReceived';
import {
  policyCancellationDelayReasons,
  PolicyCancellationDelayReasonValue,
} from './const/policyCancellationDelayReasons';
import { LoaderContext } from 'shared/context/loaderContext';
import RefundEligibilitySelect from './components/RefundEligibilitySelect';
import SubmissionReasonSelect from './components/SubmissionReasonSelect';
import CancellationSummaryDialog from './components/CancellationSummaryDialog';
import AttachmentsInput from './components/AttachmentsInput';

const PolicyCancellationLayout = () => {
  const { t } = useTranslation();
  const { policyId } = useParams();
  const { showNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { policyDetails, fetchData } = useFetchPolicyDetails({
    policyId,
    persistentQuoteId: null,
    bookingDataId: null,
  });
  const { policyRefund, refundDate, fetchRefundData } = useFetchPolicyRefund();
  const { cancelPolicy } = useCancelPolicy();
  const {
    cancellationRequest,
    fetchCancellationRequest,
    submitForCancellation,
  } = useCancellationRequest();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      cancellationRequestDate: null,
      methodReceived: null,
      delayReason: null,
      delayReasonOtherText: null,
      submissionReason: null,
      reason: null,
      refundEligibility: null,
      confirmed: false,
    },
  });

  const { loading } = useContext(LoaderContext);
  const [attachments, setAttachments] = useState([]);

  const isCancelled = useMemo(
    () =>
      policyDetails?.policy?.status?.label === 'cancelled' ||
      policyDetails?.policy?.status.status === 13,
    [policyDetails],
  );

  const cancellationRequestDate = watch('cancellationRequestDate');
  const reason = watch('reason');
  const methodReceived = watch('methodReceived');
  const confirmed = watch('confirmed');
  const delayReason = watch('delayReason');
  const delayReasonOtherText = watch('delayReasonOtherText') || '';
  const submissionReason = watch('submissionReason');
  const refundEligibility = watch('refundEligibility');

  const minCancellationRequestDate = dayjs(
    policyDetails?.policy?.subscriptionDate,
  );
  const maxCancellationRequestDate = dayjs();

  const isCancellationRequestDateBeforeToday =
    cancellationRequestDate && cancellationRequestDate.isBefore(dayjs(), 'day');
  const isCancellationRequestRequired =
    !!isCancellationRequestDateBeforeToday || !!submissionReason;

  const handleDateChange = (date) => {
    if (!date) {
      return;
    }

    if (
      date.isBefore(minCancellationRequestDate) ||
      date.isAfter(maxCancellationRequestDate) ||
      !dayjs(date).isValid()
    ) {
      setError('cancellationRequestDate', {
        message: t('POLICY_CANCELLATION.validation.dateNotInRange'),
      });
      return;
    }
    clearErrors('cancellationRequestDate');
    setValue('cancellationRequestDate', date);
  };

  const handleReceivedMethodChange = (method) => {
    setValue('methodReceived', method);
  };

  const handleDelayReasonChange = (delayReason) => {
    setValue('delayReason', delayReason);
  };

  const handleSubmissionReasonChange = (selectedReason) => {
    setValue('submissionReason', selectedReason);
  };

  const handleReasonChange = (selectedReason) => {
    setValue('reason', selectedReason);
  };

  const handleRefundEligibilityChange = (selectedRefundEligibility) => {
    setValue('refundEligibility', selectedRefundEligibility);
  };

  const handleConfirmChange = (event) => {
    setValue('confirmed', event.target.checked);
  };

  const handleDelayReasonOtherTextChange = (event) => {
    setValue('delayReasonOtherText', event.target.value);
  };

  const handleCancelPolicy = async (formData) => {
    const {
      cancellationRequestDate,
      methodReceived,
      delayReason,
      reason,
      refundEligibility: selectedRefundEligibility,
    } = formData;

    const cancellationRejectCause =
      checkForPolicyCancellationRejectCause(policyDetails);
    if (
      cancellationRejectCause === CancellationRejectCauses.POLICY_ALREADY_ENDED
    ) {
      showNotification(
        t('POLICY_DETAILS.daysPolicyCancellationLimit'),
        'error',
      );
      return Promise.reject();
    }

    const shouldAddDelayReasonOtherText =
      isCancellationRequestDateBeforeToday &&
      delayReason?.value === PolicyCancellationDelayReasonValue.OTHER;

    const formattedCancellationRequestDate = dayjs(
      cancellationRequestDate,
    ).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

    await cancelPolicy(
      policyDetails,
      policyRefund,
      refundDate,
      reason.value,
      formattedCancellationRequestDate,
      methodReceived.value,
      isCancellationRequestDateBeforeToday ? delayReason.value : null,
      shouldAddDelayReasonOtherText ? delayReasonOtherText : null,
      selectedRefundEligibility.value,
    );
    await fetchData(policyId);
  };

  const navigateToPolicyDetails = () => {
    const queryParams = new URLSearchParams(location.search);
    const persistentQuoteId = queryParams.get('persistentQuoteId');
    const searchParams = new URLSearchParams();

    if (persistentQuoteId)
      searchParams.set('persistentQuoteId', persistentQuoteId);

    navigate(`/policy-details/${policyId}?${searchParams.toString()}`);
  };

  useEffect(() => {
    // Refund calculation is rejected (500) for cancelled policies, e.g. on the post-cancel refetch.
    if (policyDetails && !isCancelled) {
      fetchRefundData(policyDetails, dayjs().toDate());
    }
  }, [policyDetails, isCancelled]);

  useEffect(() => {
    if (policyId) {
      fetchCancellationRequest(policyId);
    }
  }, [policyId, fetchCancellationRequest]);

  const hasPendingRequest =
    cancellationRequest?.requestStatus === 'PENDING_CANCELLATION';

  const handleSubmitForCancellation = async (formData) => {
    const {
      cancellationRequestDate,
      methodReceived,
      delayReason,
      reason,
      refundEligibility: selectedRefundEligibility,
    } = formData;

    const shouldAddDelayReasonOtherText =
      isCancellationRequestDateBeforeToday &&
      delayReason?.value === PolicyCancellationDelayReasonValue.OTHER;

    const formattedCancellationRequestDate = dayjs(
      cancellationRequestDate,
    ).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

    const result = await submitForCancellation(policyId, {
      refundAmount: policyRefund,
      cancellationReason: reason.value,
      cancellationRequestDate: formattedCancellationRequestDate,
      methodReceived: methodReceived.value,
      delayReason: isCancellationRequestDateBeforeToday
        ? delayReason.value
        : null,
      delayReasonOtherText: shouldAddDelayReasonOtherText
        ? delayReasonOtherText
        : null,
      submissionReason: submissionReason?.value ?? null,
      refundEligibility: selectedRefundEligibility?.value,
      attachments,
    });

    if (result) {
      navigateToPolicyDetails();
    }
  };

  const hasValidationErrors = Object.keys(errors).length > 0;

  const cancelPolicyButtonDisabled =
    loading ||
    hasValidationErrors ||
    hasPendingRequest ||
    !reason ||
    !refundEligibility ||
    !confirmed ||
    !cancellationRequestDate ||
    !methodReceived ||
    (isCancellationRequestDateBeforeToday && !delayReason) ||
    (isCancellationRequestDateBeforeToday && !submissionReason) ||
    (delayReason?.value === PolicyCancellationDelayReasonValue.OTHER &&
      (!delayReasonOtherText || delayReasonOtherText.length < 5));

  // A backdated date or a selected submission reason requires a cancellation request instead of a direct cancel.
  const cancelPolicyDisabled =
    cancelPolicyButtonDisabled || isCancellationRequestRequired;

  const submitForCancellationDisabled =
    cancelPolicyButtonDisabled || attachments.length === 0;

  const [confirmVariant, setConfirmVariant] = useState(null);
  // Retain the variant during the dialog's close animation so the cancel-only
  // "not reversible" warning doesn't flash after confirmVariant resets to null.
  const lastConfirmVariant = useRef('cancel');
  if (confirmVariant) {
    lastConfirmVariant.current = confirmVariant;
  }

  const cancellationSummary = {
    policyNumber: policyDetails?.policy?.policyNumber,
    requestDate: cancellationRequestDate
      ? dayjs(cancellationRequestDate).format('DD-MM-YYYY')
      : undefined,
    refundAmount: policyRefund != null ? `${policyRefund}` : undefined,
    reason: reason?.label ? t(reason.label) : undefined,
    methodReceived: methodReceived?.label ? t(methodReceived.label) : undefined,
    delayReason:
      isCancellationRequestDateBeforeToday && delayReason?.label
        ? t(delayReason.label)
        : undefined,
    submissionReason: submissionReason?.label
      ? t(submissionReason.label)
      : undefined,
    refundEligibility: refundEligibility?.label
      ? t(refundEligibility.label)
      : undefined,
    attachments:
      isCancellationRequestRequired && attachments.length > 0
        ? attachments.map((file) => file.name).join(', ')
        : undefined,
  };

  const handleConfirmDialog = () => {
    const action = confirmVariant;
    setConfirmVariant(null);
    if (action === 'cancel') {
      handleSubmit(handleCancelPolicy)();
    } else if (action === 'submit') {
      handleSubmit(handleSubmitForCancellation)();
    }
  };

  if (!policyDetails) {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar>
        <Breadcrumbs
          title={t('COMMON.cancel')}
          route={[
            { title: t('POLICIES.header'), link: '/policies' },
            {
              title: policyDetails?.policy.policyNumber,
              link: `/policy-details/${policyDetails?.policy.policyNumber}`,
            },
            {
              title: 'cancel',
            },
          ]}
        />
      </DashboardNavbar>
      <Box className={styles.PolicyCancellation}>
        {isCancelled ? (
          <CancelledPolicyDetails
            policyDetails={policyDetails}
            policyRefund={policyRefund}
          />
        ) : (
          <>
            <Typography variant="h2">
              {t('POLICY_CANCELLATION.cancelPolicy')}
            </Typography>
            <Typography
              variant="body2"
              className={styles.PolicyNumber}
              color="grey.700"
              sx={{ mb: 1 }}
            >
              {t('POLICY_CANCELLATION.policyNumber')}:{' '}
              <Typography
                variant="body2"
                component="span"
                color="gradients.info.main"
                fontWeight={700}
              >
                {policyDetails.policy.policyNumber}
              </Typography>
            </Typography>
            <Box className={styles.script}>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                {t('POLICY_CANCELLATION.script.title')}
              </Typography>
              <br />
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: '0.85rem' }}
              >
                {t('POLICY_CANCELLATION.script.text')}
              </Typography>
            </Box>
            <form onSubmit={handleSubmit(handleCancelPolicy)}>
              <Box className={styles.select}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {t('POLICY_CANCELLATION.label.date')}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    required
                    minDate={minCancellationRequestDate}
                    maxDate={maxCancellationRequestDate}
                    format="DD-MM-YYYY"
                    sx={{ display: 'flex', maxWidth: '15rem' }}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        error: !!errors.cancellationRequestDate,
                        helperText:
                          errors.cancellationRequestDate?.message || '',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box className={styles.select}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {t('POLICY_CANCELLATION.label.select')}
                </Typography>
                <SoftSelect
                  required
                  placeholder={t(
                    'POLICY_CANCELLATION.selectPlaceholder.select',
                  )}
                  options={policyCancellationMethodReceived}
                  onChange={handleReceivedMethodChange}
                  className="reason-dropdown"
                />
              </Box>

              {isCancellationRequestDateBeforeToday && (
                <>
                  <Box className={styles.select}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {t('POLICY_CANCELLATION.label.delay')}
                    </Typography>
                    <SoftSelect
                      value={delayReason}
                      placeholder={t(
                        'POLICY_CANCELLATION.selectPlaceholder.reason',
                      )}
                      options={policyCancellationDelayReasons}
                      onChange={handleDelayReasonChange}
                    />
                  </Box>

                  {delayReason?.value ===
                    PolicyCancellationDelayReasonValue.OTHER && (
                    <Box className={styles.select}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('POLICY_CANCELLATION.label.delayOther')}
                      </Typography>
                      <TextField
                        {...register('delayReasonOtherText')}
                        fullWidth
                        onChange={handleDelayReasonOtherTextChange}
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 50, minLength: 3 }}
                        error={delayReasonOtherText.length < 3}
                        helperText={`${delayReasonOtherText.length}/50 characters`}
                        sx={{
                          '& .MuiInputBase-root': {
                            width: '100%',
                          },
                          '& .MuiInputBase-input': {
                            padding: 0,
                            lineHeight: 1.4,
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}

              <SubmissionReasonSelect
                className={styles.select}
                value={submissionReason}
                onChange={handleSubmissionReasonChange}
                required={isCancellationRequestDateBeforeToday}
              />

              <PriceDiffSection
                policyDetails={policyDetails}
                policyRefund={policyRefund}
                isCancelled={isCancelled}
              />

              {isCancellationRequestRequired && (
                <>
                  <Typography
                    variant="body2"
                    color="gradients.info.main"
                    sx={{ fontSize: '0.85rem', mb: 2, maxWidth: '30rem' }}
                  >
                    {t(
                      isCancellationRequestDateBeforeToday
                        ? 'POLICY_CANCELLATION.submitForReviewInfo'
                        : 'POLICY_CANCELLATION.submitForReviewReasonInfo',
                    )}
                  </Typography>
                  <AttachmentsInput
                    files={attachments}
                    onChange={setAttachments}
                    disabled={loading || hasPendingRequest}
                  />
                </>
              )}

              <Box className={styles.reasonDropdown}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {t('POLICY_CANCELLATION.label.cancellation')}
                </Typography>
                <SoftSelect
                  required
                  placeholder={t(
                    'POLICY_CANCELLATION.selectPlaceholder.reason',
                  )}
                  options={policyCancellationReasons}
                  value={getValues('reason')}
                  onChange={handleReasonChange}
                  className="reason-dropdown"
                />
              </Box>

              <RefundEligibilitySelect
                className={styles.refundEligibility}
                value={refundEligibility}
                onChange={handleRefundEligibilityChange}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    {...register('confirmed')}
                    checked={confirmed}
                    onChange={handleConfirmChange}
                    name="confirmed"
                    color="primary"
                  />
                }
                label={
                  <Box className={styles.consentText}>
                    <Typography variant="body2" mb={0.5}>
                      {t('POLICY_CANCELLATION.consent.text')}
                    </Typography>
                    <List sx={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                      <ListItem sx={{ display: 'list-item', mb: 0.5 }}>
                        {t('POLICY_CANCELLATION.consent.point1')}
                      </ListItem>
                      <ListItem sx={{ display: 'list-item', mb: 0.5 }}>
                        {t('POLICY_CANCELLATION.consent.point2')}
                      </ListItem>
                    </List>
                    <Typography variant="body2" fontStyle="italic">
                      {t('POLICY_CANCELLATION.consent.disclaimer')}
                    </Typography>
                  </Box>
                }
                className={styles.consentCheckbox}
                sx={{
                  maxWidth: '40rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              />
              {hasPendingRequest && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 2, mb: 1, fontWeight: 700 }}
                >
                  {t('POLICY_CANCELLATION.pendingRequestNote')}
                </Typography>
              )}
              <Grid container spacing={2} className={styles.actions}>
                <Grid item>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigateToPolicyDetails()}
                    sx={{ '&:hover': { borderColor: colors.info.main } }}
                  >
                    {t('COMMON.back')}
                  </SoftButton>
                </Grid>
                <Grid item>
                  <SoftButton
                    type="button"
                    variant="contained"
                    color="info"
                    disabled={cancelPolicyDisabled}
                    onClick={handleSubmit(() => setConfirmVariant('cancel'))}
                    sx={{ '&:hover': { borderColor: colors.info.main } }}
                  >
                    {t('POLICY_CANCELLATION.cancelPolicy')}
                  </SoftButton>
                </Grid>
                {isCancellationRequestRequired && (
                  <Grid item>
                    <SoftButton
                      type="button"
                      variant="contained"
                      color="info"
                      disabled={submitForCancellationDisabled}
                      onClick={handleSubmit(() => setConfirmVariant('submit'))}
                      sx={{ '&:hover': { borderColor: colors.info.main } }}
                    >
                      {t('POLICY_CANCELLATION.submitForCancellation')}
                    </SoftButton>
                  </Grid>
                )}
              </Grid>
            </form>
            <CancellationSummaryDialog
              open={confirmVariant !== null}
              variant={lastConfirmVariant.current}
              summary={cancellationSummary}
              onClose={() => setConfirmVariant(null)}
              onConfirm={handleConfirmDialog}
            />
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default PolicyCancellationLayout;
